/**
 * WebView Message Handling Hook
 * Handles bidirectional communication with parent WebView
 */

import { useEffect, useCallback } from 'react';
import { WebViewMessageService } from '../services/messageService';
import { WebViewMessageType, WebViewExternalProps } from '../types';
import { useTestStore } from '../../../store/useTestStore';

export function useWebViewMessages() {
  const messageService = WebViewMessageService.getInstance();
  const { questions, groups, currentTestId, testTitle } = useTestStore();

  // Handle answer change
  const handleAnswerChange = useCallback((questionId: string, answer: any) => {
    messageService.sendAnswerChange(questionId, answer);
    
    // Call external callback if provided
    if (window.whiteboardExternalProps?.callbacks?.onAnswerChange) {
      window.whiteboardExternalProps.callbacks.onAnswerChange(questionId, answer);
    }
  }, [messageService]);

  // Handle test completion
  const handleTestComplete = useCallback((results: any) => {
    messageService.sendTestComplete(results);
    
    // Call external callback if provided
    if (window.whiteboardExternalProps?.callbacks?.onTestComplete) {
      window.whiteboardExternalProps.callbacks.onTestComplete(results);
    }
  }, [messageService]);

  // Handle recording completion
  const handleRecordingComplete = useCallback((recordingData: any) => {
    messageService.sendRecordingComplete(recordingData);
    
    // Call external callback if provided
    if (window.whiteboardExternalProps?.callbacks?.onRecordingComplete) {
      window.whiteboardExternalProps.callbacks.onRecordingComplete(recordingData);
    }
  }, [messageService]);

  // Set up message handlers
  useEffect(() => {
    // Handle exam data request
    messageService.on(WebViewMessageType.REQUEST_EXAM_DATA, () => {
      const testData = {
        questions,
        groups,
        testId: currentTestId,
        testTitle
      };
      
      if (questions.length > 0 || groups.length > 0) {
        messageService.send(WebViewMessageType.EXAM_DATA_LOADED, {
          testData,
          timestamp: Date.now(),
        });
      }
    });

    // Handle auth update
    messageService.on(WebViewMessageType.UPDATE_AUTH, (data) => {
      // Re-authenticate with new credentials
      if (data?.authConfig && window.whiteboardExternalProps) {
        window.whiteboardExternalProps = {
          ...window.whiteboardExternalProps,
          authConfig: data.authConfig,
        } as WebViewExternalProps;
        
        // Trigger re-authentication
        window.location.reload();
      }
    });

    // Handle test config update
    messageService.on(WebViewMessageType.UPDATE_TEST_CONFIG, (data) => {
      if (data?.testConfig && window.whiteboardExternalProps) {
        window.whiteboardExternalProps = {
          ...window.whiteboardExternalProps,
          testConfig: data.testConfig,
        } as WebViewExternalProps;
      }
    });

    return () => {
      messageService.off(WebViewMessageType.REQUEST_EXAM_DATA);
      messageService.off(WebViewMessageType.UPDATE_AUTH);
      messageService.off(WebViewMessageType.UPDATE_TEST_CONFIG);
    };
  }, [messageService, questions, groups, currentTestId, testTitle]);

  return {
    sendAnswerChange: handleAnswerChange,
    sendTestComplete: handleTestComplete,
    sendRecordingComplete: handleRecordingComplete,
    sendError: (error: Error) => messageService.sendError(error),
  };
}